import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { grnService } from '../../services/grnService';
import { apiService } from '../../services/api';
import { GRNDTO, UpdateGRNRequest, UpdateGRNItemRequest } from '../../types';
import './GRNPage.css';
import { ArrowLeft, Save } from 'lucide-react';

interface WarehouseOption { id: number; name: string; }

const GRNEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [grn, setGrn]             = useState<GRNDTO | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);

  // Editable header fields
  const [receiptDate, setReceiptDate] = useState('');
  const [warehouseId, setWarehouseId] = useState(0);
  const [status, setStatus]           = useState('');
  const [notes, setNotes]             = useState('');

  // Editable items
  const [items, setItems] = useState<UpdateGRNItemRequest[]>([]);

  useEffect(() => {
    const init = async () => { await Promise.all([fetchGRN(), fetchWarehouses()]); };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchGRN = async () => {
    try {
      const res = await grnService.getGRNById(id!);
      const data = res.data?.data;
      if (data) {
        setGrn(data);
        setReceiptDate(data.receiptDate.split('T')[0]);
        setWarehouseId(data.warehouseId);
        setStatus(data.status);
        setNotes(data.notes ?? '');
        setItems(
          data.grnItems.map((item) => ({
            grnItemId:        item.grnItemId,
            quantityReceived: item.quantityReceived,
            qualityStatus:    item.qualityStatus,
            batchNumber:      item.batchNumber ?? '',
            expiryDate:       item.expiryDate?.split('T')[0] ?? '',
            manufactureDate:  item.manufactureDate?.split('T')[0] ?? '',
            remarks:          item.remarks ?? '',
            goodQuantity:     item.goodQuantity,
            defectiveQuantity: item.defectiveQuantity,
          }))
        );
      }
    } catch {
      setError(t('grn.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      // Warehouses live under api/v1/organization/warehouses — the short
      // /warehouses path 404s.
      const res = await apiService.get<any>('/organization/warehouses', { pageSize: 200 });
      const raw = res.data?.data?.data ?? res.data?.data ?? [];
      setWarehouses(raw.map((w: any) => ({ id: w.id ?? w.warehouseId, name: w.name ?? w.warehouseName })));
    } catch {
      // non-fatal
    }
  };

  const handleItemChange = (index: number, field: keyof UpdateGRNItemRequest, value: unknown) => {
    setItems((prev) => {
      const next = [...prev];
      (next[index] as Record<string, unknown>)[field] = value;
      if (field === 'quantityReceived' || field === 'defectiveQuantity') {
        const item = next[index];
        next[index].goodQuantity = Math.max(0, item.quantityReceived - item.defectiveQuantity);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grn || !user) return;
    setSaving(true);
    setError(null);
    try {
      const payload: UpdateGRNRequest = {
        receiptDate,
        warehouseId,
        notes: notes || undefined,
        status,
        receivedByUserId: user.id,
        verifiedByUserId: grn.verifiedByUserId ?? undefined,
        grnItems: items.map((item) => ({
          ...item,
          expiryDate:      item.expiryDate      || undefined,
          manufactureDate: item.manufactureDate || undefined,
          batchNumber:     item.batchNumber     || undefined,
          remarks:         item.remarks         || undefined,
        })),
      };
      await grnService.updateGRN(grn.grnId, payload);
      navigate(`/grn/${grn.grnId}`, { state: { message: t('grn.updatedSuccess') } });
    } catch {
      setError(t('grn.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="ims-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Not found ──────────────────────────────────────────────────────────────
  if (!grn) {
    return (
      <Layout>
        <div style={{ padding: '32px 28px' }}>
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            {error || t('grn.notFound')}
          </div>
          <button className="btn-cancel" onClick={() => navigate('/grn')}>
            {t('grn.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <form onSubmit={handleSubmit} className="grn-create-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(`/grn/${grn.grnId}`)}>
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('common.back')}
            </button>
            <div>
              <h1 className="ims-page-title">{t('grn.editTitle')}</h1>
              <p className="ims-page-subtitle"><code>{grn.grnNumber}</code></p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="btn-cancel"
              onClick={() => navigate(`/grn/${grn.grnId}`)} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={14} strokeWidth={2} />
                  {t('common.saveChanges')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* ─── LEFT — Header fields ─── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('grn.grnInfo')}</h2>
              </div>
              <div className="ims-card-body">

                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.receiptDate')} <span className="required">*</span></label>
                  <input type="date" className="ims-form-control" value={receiptDate}
                    onChange={(e) => setReceiptDate(e.target.value)} required />
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.warehouse')} <span className="required">*</span></label>
                  <select className="ims-form-control" value={warehouseId}
                    onChange={(e) => setWarehouseId(Number(e.target.value))} required>
                    <option value={0}>{t('grn.selectWarehouse')}</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.statusLabel')} <span className="required">*</span></label>
                  <select className="ims-form-control" value={status}
                    onChange={(e) => setStatus(e.target.value)} required>
                    <option value="Draft">{t('grn.statusDraft')}</option>
                    <option value="Received">{t('grn.statusReceived')}</option>
                    <option value="Verified">{t('grn.statusVerified')}</option>
                    <option value="Completed">{t('grn.statusCompleted')}</option>
                  </select>
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.notes')}</label>
                  <textarea className="ims-form-control ims-textarea" value={notes}
                    onChange={(e) => setNotes(e.target.value)} rows={3} />
                </div>

              </div>
            </div>
          </div>

          {/* ─── CENTER — Items table ─── */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">
                  {t('grn.receiptItemsCount')}
                  <span style={{ marginInlineStart: 8, fontWeight: 400, color: 'var(--ims-text-muted)', fontSize: 13 }}>
                    ({items.length})
                  </span>
                </h2>
              </div>
              <div className="ims-lines-body">
                <div className="ims-table-scroll">
                  <table className="ims-table">
                    <thead>
                      <tr>
                        <th className="ims-th" style={{ minWidth: 140 }}>{t('grn.product')}</th>
                        <th className="ims-th ims-th--num">{t('grn.qtyReceived')}</th>
                        <th className="ims-th" style={{ minWidth: 120 }}>{t('grn.qualityStatus')}</th>
                        <th className="ims-th ims-th--num" style={{ color: 'var(--ims-green-text)' }}>{t('grn.goodQty')}</th>
                        <th className="ims-th ims-th--num" style={{ color: 'var(--ims-red-text)' }}>{t('grn.defectiveQty')}</th>
                        <th className="ims-th" style={{ minWidth: 130 }}>{t('grn.batchNumber')}</th>
                        <th className="ims-th" style={{ minWidth: 140 }}>{t('grn.manufactureDate')}</th>
                        <th className="ims-th" style={{ minWidth: 140 }}>{t('grn.expiryDate')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const original = grn.grnItems[index];
                        return (
                          <tr key={item.grnItemId} className="ims-tr">
                            {/* Product (read-only) */}
                            <td className="ims-td">
                              <div className="ims-product-name">{original?.productName ?? `Item #${item.grnItemId}`}</div>
                              {original?.productCode && (
                                <div className="ims-product-code">{original.productCode}</div>
                              )}
                            </td>
                            {/* Qty Received */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0}
                                value={item.quantityReceived}
                                onChange={(e) => handleItemChange(index, 'quantityReceived', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Quality Status */}
                            <td className="ims-td">
                              <select className="ims-cell-input" value={item.qualityStatus}
                                onChange={(e) => handleItemChange(index, 'qualityStatus', e.target.value)}>
                                <option value="Good">{t('grn.qualityGood')}</option>
                                <option value="Partial">{t('grn.qualityPartial')}</option>
                                <option value="Defective">{t('grn.qualityDefective')}</option>
                              </select>
                            </td>
                            {/* Good Qty (auto) */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input ims-good-input"
                                value={item.goodQuantity} disabled />
                            </td>
                            {/* Defective */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0}
                                value={item.defectiveQuantity}
                                onChange={(e) => handleItemChange(index, 'defectiveQuantity', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Batch */}
                            <td className="ims-td">
                              <input type="text" className="ims-cell-input"
                                value={item.batchNumber ?? ''}
                                onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                                placeholder={t('grn.batchNumberPlaceholder')} />
                            </td>
                            {/* Manufacture Date */}
                            <td className="ims-td">
                              <input type="date" className="ims-cell-input"
                                value={item.manufactureDate ?? ''}
                                onChange={(e) => handleItemChange(index, 'manufactureDate', e.target.value)} />
                            </td>
                            {/* Expiry Date */}
                            <td className="ims-td">
                              <input type="date" className="ims-cell-input"
                                value={item.expiryDate ?? ''}
                                onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* ─── RIGHT — Actions + GRN Info ─── */}
          <div className="ims-panel-right">

            {/* Save / Cancel */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('common.actions')}</h3>
              </div>
              <div className="ims-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button type="submit" className="btn-save" disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
                  {saving ? (
                    <><span className="ims-btn-spinner" />{t('common.saving')}</>
                  ) : (
                    <>
                      <Save size={14} strokeWidth={2} />
                      {t('common.saveChanges')}
                    </>
                  )}
                </button>
                <button type="button" className="btn-cancel" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => navigate(`/grn/${grn.grnId}`)} disabled={saving}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>

            {/* GRN Info */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('grn.grnInfo')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.grnNumber')}</span>
                  <span className="ims-summary-value"><code style={{ fontSize: 11 }}>{grn.grnNumber}</code></span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.type')}</span>
                  <span className="ims-summary-value">{grn.grnType}</span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.supplier')}</span>
                  <span className="ims-summary-value" style={{ fontSize: 12 }}>{grn.supplierName || '—'}</span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.items')}</span>
                  <span className="ims-summary-value">{grn.grnItems.length}</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </form>
    </Layout>
  );
};

export default GRNEditPage;
